import React from "react";
import { Heart, Shield, Zap, Target, Clock, TrendingUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const longevityServices = [{
  icon: Heart,
  title: "Energy Reset Program",
  description: "Feel naturally energetic and alert throughout the day.",
  benefits: ["Wake up rested and clear-headed instead of groggy or foggy", "Eliminate afternoon crashes and dependence on caffeine", "Consistent energy for work, workouts, and family time"]
}, {
  icon: Shield,
  title: "Body Recomposition Track",
  description: "Lose fat, keep muscle — regardless of age.",
  benefits: ["Reduce stubborn body fat without sacrificing strength or performance", "Preserve (or rebuild) lean muscle while improving metabolism", "Look athletic again, not 'smaller' or depleted"]
}, {
  icon: Zap,
  title: "Performance Strength Boost",
  description: "Train harder, recover faster, and feel physically capable again.",
  benefits: ["Increase strength, power, and workout intensity", "Faster recovery between training sessions and fewer setbacks", "Feel capable and resilient instead of worn down"]
}, {
  icon: Target,
  title: "Confidence & Vitality Restored",
  description: "Libido, mood, and mental clarity that matches the way you want to feel.",
  benefits: ["Stronger libido and sexual confidence without anxiety or inconsistency", "Improved mood, motivation, and emotional steadiness", "Sharper focus and mental clarity throughout the day"]
}, {
  icon: Clock,
  title: "Healthy Aging & Longevity Track",
  description: "Age with strength, clarity, and independence — not decline.",
  benefits: ["Maintain muscle, bone strength, and metabolic health as you age", "Support long-term cardiovascular and cognitive function", "Stay active, capable, and confident well into later decades"]
}, {
  icon: TrendingUp,
  title: "Metabolic & Hormonal Reset",
  description: "Fix what's holding you back beneath the surface.",
  benefits: ["Improve insulin sensitivity, appetite control, and body regulation", "Normalize hormonal imbalances affecting energy, sleep, and weight", "Create a stable internal environment that supports long-term results"]
}];
export default function LongevitySolutions() {
  React.useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.type = 'text/javascript';
    script.async = true;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(script);
    };
  }, []);
  const handleCalendlyClick = () => {
    if ((window as any).Calendly) {
      (window as any).Calendly.initPopupWidget({
        url: 'https://calendly.com/mfloresdo/30min'
      });
    }
  };
  return <section className="py-16 bg-background">
      <div className="container">
        {}
        <div className="text-center mb-12 max-w-4xl mx-auto">
          <div className="border border-muted-light rounded-lg p-6 bg-card shadow-soft">
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">
              Do any of these sound like you?
            </h3>
            <p className="text-base md:text-lg text-muted-dark leading-relaxed mb-8 italic">
              If so, it's not a lack of discipline — it's often physiology.
            </p>
            <ul className="text-left grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              <li className="flex items-start text-base md:text-lg text-muted-dark leading-relaxed">
                <span className="mr-3 mt-1.5">•</span>
                <span>Stubborn belly fat even with training</span>
              </li>
              <li className="flex items-start text-base md:text-lg text-muted-dark leading-relaxed">
                <span className="mr-3 mt-1.5">•</span>
                <span>Persistent fatigue even with good sleep</span>
              </li>
              <li className="flex items-start text-base md:text-lg text-muted-dark leading-relaxed">
                <span className="mr-3 mt-1.5">•</span>
                <span>Slower recovery and declining performance</span>
              </li>
              <li className="flex items-start text-base md:text-lg text-muted-dark leading-relaxed">
                <span className="mr-3 mt-1.5">•</span>
                <span>Weak libido or lost drive</span>
              </li>
            </ul>
          </div>
        </div>

        {}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 leading-snug">
            Our Transformation Tracks
          </h2>
          <p className="text-base md:text-lg text-muted-dark leading-relaxed max-w-3xl mx-auto mb-8">
            At IronPeak Men's Health, we help men restore energy, rebuild strength, and feel confident in their bodies again — using personalized, physician-led care designed for long-term results.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {longevityServices.map((service, i) => {
          const IconComponent = service.icon;
          return <Card key={i} data-index={i} className="card hover-lift bg-card border border-muted-light shadow-soft">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary-light-bg rounded-lg flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-muted-dark leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.benefits.map((benefit, benefitIndex) => <li key={benefitIndex} data-index={benefitIndex} className="flex items-start text-sm text-muted-dark">
                        <div className="w-1.5 h-1.5 bg-secondary rounded-full mr-3 flex-shrink-0 mt-1.5"></div>
                        <span>{benefit}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>;
        })}
        </div>

        {}
        <div className="text-center">
          <div className="bg-primary-light-bg rounded-lg p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
              Ready to Transform Your Health Journey?
            </h3>
            <p className="text-base md:text-lg text-muted-dark leading-relaxed mb-6">Join Texas men who have regained their confidence and energy through our personalized longevity solutions. Experience the transformation with IronPeak Men's Health today.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button size="lg" className="btn-primary" onClick={handleCalendlyClick}>
                Start Your Energy & Performance Reset
              </Button>
              <Button variant="outline" size="lg" className="border border-primary text-primary hover:bg-primary-light-bg" asChild>
                
              </Button>
            </div>
            <p className="text-sm md:text-base text-muted-dark leading-relaxed mt-4 italic text-center">
              Physician-led care. Secure telemedicine. State-compliant medical oversight.
            </p>
          </div>
        </div>
      </div>
    </section>;
}